import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';

const NotificationSettings = ({ onSaveSettings }) => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    smsNotifications: false,
    notifyOnCompletion: true,
    notifyOnError: true,
    notifyOnQueue: false,
    email: 'user@example.com',
    phone: '+1 (555) 123-4567'
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSaveSettings(settings);
    // Show success message or toast
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">Notification Settings</h2>
            <p className="text-sm text-muted-foreground">Configure render completion alerts</p>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
        </Button>
      </div>
      {/* Quick Settings */}
      <div className="space-y-4 mb-6">
        <Checkbox
          label="Email notifications"
          description="Get notified via email when renders complete"
          checked={settings?.emailNotifications}
          onChange={(e) => handleSettingChange('emailNotifications', e?.target?.checked)}
        />
        
        <Checkbox
          label="Notify on completion"
          description="Receive alerts when video rendering finishes"
          checked={settings?.notifyOnCompletion}
          onChange={(e) => handleSettingChange('notifyOnCompletion', e?.target?.checked)}
        />
      </div>
      {/* Expanded Settings */}
      {isExpanded && (
        <div className="space-y-6 pt-6 border-t border-border">
          {/* Notification Methods */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Notification Methods</h3>
            <div className="space-y-3">
              <Checkbox
                label="Push notifications"
                description="Browser notifications (requires permission)"
                checked={settings?.pushNotifications}
                onChange={(e) => handleSettingChange('pushNotifications', e?.target?.checked)}
              />
              
              <Checkbox
                label="SMS notifications"
                description="Text message alerts for important updates"
                checked={settings?.smsNotifications}
                onChange={(e) => handleSettingChange('smsNotifications', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Notification Types */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Notification Types</h3>
            <div className="space-y-3">
              <Checkbox
                label="Render errors"
                description="Alert me when renders fail or encounter errors"
                checked={settings?.notifyOnError}
                onChange={(e) => handleSettingChange('notifyOnError', e?.target?.checked)}
              />
              
              <Checkbox
                label="Queue updates"
                description="Notify when renders move up in the queue"
                checked={settings?.notifyOnQueue}
                onChange={(e) => handleSettingChange('notifyOnQueue', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-sm font-medium text-foreground mb-3">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Email Address"
                type="email"
                value={settings?.email}
                onChange={(e) => handleSettingChange('email', e?.target?.value)}
                disabled={!settings?.emailNotifications}
              />
              
              <Input
                label="Phone Number"
                type="tel"
                value={settings?.phone}
                onChange={(e) => handleSettingChange('phone', e?.target?.value)}
                disabled={!settings?.smsNotifications}
              />
            </div>
          </div>

          {/* Preview */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Mail" size={16} className="text-primary mt-1" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-foreground">Preview Notification</h4>
                <p className="text-xs text-muted-foreground mt-1">
                  "Your video 'Indie Film Credits' has finished rendering and is ready for download. 
                  File size: 245 MB • Format: MP4 • Resolution: 1080p"
                </p>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              variant="default"
              iconName="Save"
              iconPosition="left"
              onClick={handleSave}
            >
              Save Settings
            </Button>
          </div>
        </div>
      )}
      {/* Current Status */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-2 text-muted-foreground">
          <Icon name="CheckCircle" size={16} className="text-success" />
          <span>Notifications enabled</span>
        </div>
        
        <Button variant="ghost" size="sm" iconName="TestTube">
          Send Test
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettings;